import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CheckCircle, 
  Clock,
  Mail,
  Phone,
  ArrowRight,
  Download,
  Calendar
} from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Function to get URL parameters
function getUrlParameter(name) {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(window.location.search);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

// Function to poll payment status
async function pollPaymentStatus(sessionId, setPaymentStatus, attempts = 0) {
  const maxAttempts = 5;
  const pollInterval = 2000; // 2 seconds

  if (attempts >= maxAttempts) {
    setPaymentStatus({
      status: 'timeout',
      message: 'La verificación del pago tardó más de lo esperado. Por favor revisa tu email para la confirmación.'
    });
    return;
  }

  try {
    const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
    const data = response.data;
    
    if (data.payment_status === 'paid') {
      setPaymentStatus({
        status: 'success',
        message: '¡Pago exitoso! Gracias por tu compra.',
        amount: data.amount_total / 100, // Convert from cents
        currency: data.currency.toUpperCase()
      });
      return;
    } else if (data.status === 'expired') {
      setPaymentStatus({
        status: 'expired',
        message: 'La sesión de pago expiró. Por favor intenta de nuevo.'
      });
      return;
    }

    // If payment is still pending, continue polling
    setPaymentStatus({
      status: 'pending',
      message: 'Verificando el estado del pago...'
    });
    
    setTimeout(() => pollPaymentStatus(sessionId, setPaymentStatus, attempts + 1), pollInterval);
    
  } catch (error) {
    console.error('Error checking payment status:', error);
    setPaymentStatus({
      status: 'error',
      message: 'Error al verificar el estado del pago. Por favor contacta con soporte.'
    });
  }
}

const PaymentSuccess = () => {
  const [paymentStatus, setPaymentStatus] = useState({
    status: 'checking',
    message: 'Verificando el estado de tu pago...'
  });

  useEffect(() => {
    const sessionId = getUrlParameter('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId, setPaymentStatus);
    } else {
      setPaymentStatus({
        status: 'error',
        message: 'No se encontró información de la sesión de pago.'
      });
    }
  }, []);

  const getStatusIcon = () => {
    switch (paymentStatus.status) {
      case 'success':
        return <CheckCircle size={64} className="text-red-600" />;
      case 'pending':
      case 'checking':
        return <Clock size={64} className="text-yellow-600 animate-pulse" />;
      default:
        return <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <span className="text-red-600 text-2xl">!</span>
        </div>;
    }
  };

  const getStatusColor = () => {
    switch (paymentStatus.status) {
      case 'success':
        return 'text-red-600';
      case 'pending':
      case 'checking':
        return 'text-yellow-600';
      default:
        return 'text-red-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        
        {/* Main Status Card */}
        <Card className="border-0 shadow-2xl bg-white mb-8">
          <CardHeader className="text-center pb-8 pt-12">
            <div className="flex justify-center mb-6">
              {getStatusIcon()}
            </div>
            
            <CardTitle className={`text-3xl font-bold ${getStatusColor()} mb-4`}>
              {paymentStatus.status === 'success' && '¡Pago Exitoso!'}
              {paymentStatus.status === 'pending' && 'Procesando Pago...'}
              {paymentStatus.status === 'checking' && 'Verificando Pago...'}
              {paymentStatus.status === 'error' && 'Error en el Pago'}
              {paymentStatus.status === 'expired' && 'Sesión Expirada'}
              {paymentStatus.status === 'timeout' && 'Verificación Tardía'}
            </CardTitle>
            
            <p className="text-lg text-slate-600 max-w-md mx-auto">
              {paymentStatus.message}
            </p>

            {paymentStatus.amount && (
              <Badge className="mt-4 bg-red-100 text-red-800 px-6 py-2 text-lg">
                ${paymentStatus.amount} {paymentStatus.currency}
              </Badge>
            )}
          </CardHeader>
          
          <CardContent className="space-y-6 pb-12">
            {paymentStatus.status === 'success' && (
              <div className="space-y-6">
                {/* Next Steps */}
                <div className="bg-red-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-4">
                    Próximos Pasos:
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-red-600 flex-shrink-0" />
                      <span className="text-red-800">Recibirás un email de confirmación en los próximos 5 minutos</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-red-600 flex-shrink-0" />
                      <span className="text-red-800">Nuestro equipo te contactará en 24 horas para coordinar tu proyecto online</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-red-600 flex-shrink-0" />
                      <span className="text-red-800">Agendaremos una videollamada inicial si es necesario</span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    Información de Contacto:
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Mail size={20} className="text-slate-600" />
                      <span className="text-slate-700">info@drywall-design.com</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone size={20} className="text-slate-600" />
                      <span className="text-slate-700">+1 (505) 226-0487</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3"
                    onClick={() => window.location.href = '/'}
                  >
                    <ArrowRight size={20} className="mr-2" />
                    Volver al Inicio
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="flex-1 border-red-600 text-red-600 hover:bg-red-50 py-3"
                    onClick={() => window.print()}
                  >
                    <Download size={20} className="mr-2" />
                    Imprimir Recibo
                  </Button>
                </div>
              </div>
            )}

            {paymentStatus.status === 'error' && (
              <div className="space-y-4">
                <div className="bg-red-50 rounded-lg p-6 text-center">
                  <p className="text-red-800 mb-4">
                    Si el dinero fue debitado de tu cuenta, será reembolsado automáticamente en 3-5 días hábiles.
                  </p>
                  <Button 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => window.location.href = '/#paquetes'}
                  >
                    Intentar de Nuevo
                  </Button>
                </div>
              </div>
            )}

            {(paymentStatus.status === 'expired' || paymentStatus.status === 'timeout') && (
              <div className="space-y-4">
                <Button 
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
                  onClick={() => window.location.href = '/#paquetes'}
                >
                  Volver a Intentar Pago
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Additional Help */}
        <Card className="border-0 shadow-lg bg-white">
          <CardContent className="p-6 text-center">
            <h3 className="font-semibold text-slate-900 mb-2">
              ¿Necesitas Ayuda?
            </h3>
            <p className="text-slate-600 text-sm mb-4">
              Si tienes preguntas sobre tu pago o proyecto, contáctanos directamente.
            </p>
            <div className="flex justify-center gap-4">
              <a 
                href="mailto:info@drywall-design.com" 
                className="text-red-600 hover:underline text-sm"
              >
                Enviar Email
              </a>
              <a 
                href="tel:+15052260487" 
                className="text-red-600 hover:underline text-sm"
              >
                Llamar Ahora
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;